import { ApprovalStatus } from './approval-status';
import { isPurchaseOrderDispatched, type DispatchableOrder } from './purchase-order-status';
import { isRequisitionOrdered, type OrderableRequisition } from './requisition-status';

/** Where a purchase order's completion may start from: approved, or approved and sent. */
export const PurchaseOrderCompletionBaseStatus = {
  APPROVED: 'APPROVED',
  DISPATCHED: 'DISPATCHED'
} as const;

export type PurchaseOrderCompletionBaseStatus =
  (typeof PurchaseOrderCompletionBaseStatus)[keyof typeof PurchaseOrderCompletionBaseStatus];

/** The purchase order completion base statuses, in lifecycle order. */
export const PURCHASE_ORDER_COMPLETION_BASE_STATUSES = [
  PurchaseOrderCompletionBaseStatus.APPROVED,
  PurchaseOrderCompletionBaseStatus.DISPATCHED
] as const;

/** Where a requisition's completion may start from: approved, or approved with every line ordered. */
export const RequisitionCompletionBaseStatus = {
  APPROVED: 'APPROVED',
  ORDERED: 'ORDERED'
} as const;

export type RequisitionCompletionBaseStatus =
  (typeof RequisitionCompletionBaseStatus)[keyof typeof RequisitionCompletionBaseStatus];

/** The requisition completion base statuses, in lifecycle order. */
export const REQUISITION_COMPLETION_BASE_STATUSES = [
  RequisitionCompletionBaseStatus.APPROVED,
  RequisitionCompletionBaseStatus.ORDERED
] as const;

/** Days after completion a document is archived by default. */
export const DEFAULT_ARCHIVE_AFTER_DAYS = 90;

/** The longest wait before archiving a completed document, in days. */
export const MAX_ARCHIVE_AFTER_DAYS = 3650;

/**
 * When a document counts as completed — a reached status plus what has to be delivered and
 * invoiced — and whether and when completing it archives it.
 */
export interface CompletionSettings<TBaseStatus extends string> {
  /** The status the document has to have reached; a later one counts too. */
  readonly baseStatus: TBaseStatus;
  readonly isDeliveryRequired: boolean;
  readonly isInvoicingRequired: boolean;
  /** A completed document is archived. */
  readonly isArchivingOnCompletion: boolean;
  /** Days after completion it is archived; 0 archives in the completing write. */
  readonly archiveAfterDays: number;
}

export const DEFAULT_PURCHASE_ORDER_COMPLETION: CompletionSettings<PurchaseOrderCompletionBaseStatus> =
  {
    baseStatus: PurchaseOrderCompletionBaseStatus.DISPATCHED,
    isDeliveryRequired: true,
    isInvoicingRequired: true,
    isArchivingOnCompletion: true,
    archiveAfterDays: DEFAULT_ARCHIVE_AFTER_DAYS
  };

export const DEFAULT_REQUISITION_COMPLETION: CompletionSettings<RequisitionCompletionBaseStatus> = {
  baseStatus: RequisitionCompletionBaseStatus.ORDERED,
  isDeliveryRequired: false,
  isInvoicingRequired: false,
  isArchivingOnCompletion: true,
  archiveAfterDays: DEFAULT_ARCHIVE_AFTER_DAYS
};

/** Whether a purchase order has reached `baseStatus`; `DISPATCHED` is approved and sent. */
export function hasPurchaseOrderReached(
  order: { readonly approvalStatus: ApprovalStatus } & DispatchableOrder,
  baseStatus: PurchaseOrderCompletionBaseStatus
): boolean {
  if (order.approvalStatus !== ApprovalStatus.APPROVED) return false;
  return (
    baseStatus === PurchaseOrderCompletionBaseStatus.APPROVED || isPurchaseOrderDispatched(order)
  );
}

/** Whether a requisition has reached `baseStatus`; `ORDERED` is approved with every line ordered. */
export function hasRequisitionReached(
  requisition: { readonly approvalStatus: ApprovalStatus } & OrderableRequisition,
  baseStatus: RequisitionCompletionBaseStatus
): boolean {
  if (requisition.approvalStatus !== ApprovalStatus.APPROVED) return false;
  return (
    baseStatus === RequisitionCompletionBaseStatus.APPROVED || isRequisitionOrdered(requisition)
  );
}

/** Whether a purchase order with this approval status may be completed by hand. */
export const isPurchaseOrderCompletable = (approvalStatus: ApprovalStatus): boolean =>
  hasPurchaseOrderReached({ approvalStatus }, PurchaseOrderCompletionBaseStatus.APPROVED);

/** Whether a requisition with this approval status may be completed by hand. */
export const isRequisitionCompletable = (approvalStatus: ApprovalStatus): boolean =>
  hasRequisitionReached({ approvalStatus }, RequisitionCompletionBaseStatus.APPROVED);
